import { Box, Button } from "@mui/material";
import { Code as CodeIcon, MenuBook as MenuBookIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useCallback, useState } from "react";
import { HoloPad } from "../components/HoloPad";

type DocumentItem =
  | {
      type: "image";
      src: string;
      alt: string;
      rotate: string;
      scale?: number;
      offsetX?: string;
      offsetY?: string;
      pinned?: boolean;
    }
  | {
      type: "holopad";
      id: string;
      rotate: string;
      scale?: number;
      offsetX?: string;
      offsetY?: string;
      pinned?: boolean;
    };

const documents: DocumentItem[] = [
  { type: "image", src: "/lab-notebook.png", alt: "Lab notebook page", rotate: "1.5deg" },
  {
    type: "image",
    src: "/lab-change-order.png",
    alt: "OCP change order — access control",
    rotate: "-1.5deg",
  },
  {
    type: "image",
    src: "/arasaka-service-bulletin.png",
    alt: "Arasaka service bulletin",
    rotate: "2.5deg",
    scale: 0.85,
    offsetX: "15%",
  },
  {
    type: "holopad",
    id: "watchlist-growthforge",
    rotate: "-4deg",
    scale: 0.82,
    offsetX: "-20%",
  },
  {
    type: "image",
    src: "/clip-reactor-tag.png",
    alt: "Clip Reactor danger tag",
    rotate: "3deg",
    scale: 0.55,
    offsetX: "-40%",
    offsetY: "160px",
  },
  {
    type: "image",
    src: "/chat-astro-mattress-tag.png",
    alt: "Chat Astro mattress tag",
    rotate: "-7deg",
    scale: 1.0,
    offsetX: "20%",
    offsetY: "45%",
    pinned: true,
  },
];

/**
 * Home page with lab notebook image.
 */
export function ArtifactsPage() {
  // zOrder[i] = z-index for document i. Clicking a document moves it to the top.
  // Pinned documents have a minimum z-index so they always peek out.
  const pinnedMin = 1;
  const [zOrder, setZOrder] = useState(() => {
    const order = documents.map((_, i) => i);
    // Put the last non-pinned document on top
    const topIdx = [...documents].reverse().findIndex((d) => !d.pinned);
    if (topIdx !== -1) {
      const idx = documents.length - 1 - topIdx;
      const swapIdx = documents.length - 1;
      const tmp = order[idx];
      order[idx] = order[swapIdx];
      order[swapIdx] = tmp;
    }
    return order;
  });
  const bringToFront = useCallback(
    (index: number) => {
      setZOrder((prev) => {
        const current = prev[index];
        const next = prev.map((z, i) => {
          if (i === index) return documents.length - 1;
          if (z > current) return z - 1;
          return z;
        });
        // Enforce minimum z-index for pinned documents, pushing conflicts down
        const result = [...next];
        for (let i = 0; i < result.length; i++) {
          if (documents[i].pinned && result[i] < pinnedMin) {
            const displaced = result[i];
            result[i] = pinnedMin;
            // Push the document that was at pinnedMin down to the displaced slot
            for (let j = 0; j < result.length; j++) {
              if (j !== i && result[j] === pinnedMin) {
                result[j] = displaced;
                break;
              }
            }
          }
        }
        return result;
      });
    },
    [pinnedMin]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 200px)",
        py: 2,
        gap: 3,
      }}
    >
      <Box
        sx={{
          position: "relative",
          maxWidth: "90%",
          maxHeight: "75vh",
          display: "inline-block",
        }}
      >
        {/* Invisible spacer to maintain layout size */}
        <Box
          component="img"
          src="/lab-notebook.png"
          alt=""
          sx={{
            maxWidth: "100%",
            maxHeight: "75vh",
            width: "auto",
            height: "auto",
            visibility: "hidden",
          }}
        />
        {documents.map((doc, i) =>
          doc.type === "image" ? (
            <Box
              key={doc.src}
              component="img"
              src={doc.src}
              alt={doc.alt}
              onClick={() => bringToFront(i)}
              sx={{
                position: "absolute",
                top: doc.offsetY || 0,
                left: doc.offsetX || 0,
                maxWidth: "100%",
                maxHeight: "75vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                transform: `rotate(${doc.rotate})${doc.scale ? ` scale(${doc.scale})` : ""}`,
                filter:
                  zOrder[i] === documents.length - 1
                    ? "drop-shadow(0 6px 24px rgba(0, 0, 0, 0.2))"
                    : "drop-shadow(0 4px 20px rgba(0, 0, 0, 0.15))",
                zIndex: zOrder[i],
                cursor: "pointer",
              }}
            />
          ) : (
            <Box
              key={doc.id}
              onClick={() => bringToFront(i)}
              sx={{
                position: "absolute",
                top: doc.offsetY || 0,
                left: doc.offsetX || 0,
                height: "75vh",
                aspectRatio: "956 / 1324",
                transform: `rotate(${doc.rotate})${doc.scale ? ` scale(${doc.scale})` : ""}`,
                filter:
                  zOrder[i] === documents.length - 1
                    ? "drop-shadow(0 6px 24px rgba(0, 0, 0, 0.4))"
                    : "drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3))",
                zIndex: zOrder[i],
                cursor: "pointer",
              }}
            >
              <HoloPad>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {/* Animated Header */}
                  <Box
                    sx={{
                      "@keyframes glowPulse": {
                        "0%, 100%": {
                          textShadow: "0 0 10px #00ffcc, 0 0 20px #00ffcc, 0 0 30px #00ffcc",
                        },
                        "50%": {
                          textShadow: "0 0 5px #00ffcc, 0 0 10px #00ffcc, 0 0 15px #00ffcc",
                        },
                      },
                      color: "#00ffcc",
                      fontFamily: "'Courier New', monospace",
                      fontSize: "clamp(9px, 4.8cqi, 20px)",
                      fontWeight: "bold",
                      letterSpacing: "0.2em",
                      animation: "glowPulse 2s ease-in-out infinite",
                      mb: 1,
                    }}
                  >
                    ◆ WATCHLIST GROWTHFORGE ◆
                  </Box>

                  {/* Main Content */}
                  <Box
                    component="pre"
                    sx={{
                      "@keyframes subtleGlow": {
                        "0%, 100%": {
                          textShadow: "0 0 8px rgba(0, 255, 204, 0.6)",
                        },
                        "50%": {
                          textShadow: "0 0 12px rgba(0, 255, 204, 0.8)",
                        },
                      },
                      color: "#00ffcc",
                      fontFamily: "'Courier New', monospace",
                      fontSize: "clamp(5px, 2.5cqi, 13px)",
                      lineHeight: 1.3,
                      margin: 0,
                      animation: "subtleGlow 3s ease-in-out infinite",
                      whiteSpace: "pre",
                      overflow: "hidden",
                    }}
                  >
                    {`╔══════════════════════════════════════════════════════════════════╗
║  ▓▓▓ GROWTHFORGE ▓▓▓   ░░ Watchlist Intelligence Module ░░     ║
╠══════════════════════════════════════════════════════════════════╣
║                                                                  ║
║  GrowthForge is an AI-augmented watchlist expansion tool         ║
║  designed for media monitoring operators. It analyzes your       ║
║  existing TVEyes watchlist — the terms, clusters, and query      ║
║  structures you've already built — and synthesizes new keyword   ║
║  suggestions you may not have considered. Terms surface in       ║
║  real time as recommendations, ready to be reviewed, dismissed   ║
║  with feedback, or accepted directly into your live watchlist    ║
║  with a single action.                                           ║
║                                                                  ║
║  Every suggestion accepted through GrowthForge is tagged with    ║
║  an origin cross-reference, creating a traceable lineage         ║
║  between the AI pipeline and your production watchlist. The      ║
║  system learns from dismissals, refining future output based     ║
║  on what you reject and why. GrowthForge doesn't replace your    ║
║  editorial judgment — it accelerates it, turning a static list   ║
║  of monitored terms into a living, expanding sensor network.     ║
║                                                                  ║
╠══════════════════════════════════════════════════════════════════╣
║  ▲ ACCESS CONTROL                                                ║
╠══════════════════════════════════════════════════════════════════╣
║                                                                  ║
║  GrowthForge is a gated labs feature. To enable access for a     ║
║  user, the following must be present in their Auth0              ║
║  app_metadata:                                                   ║
║                                                                  ║
║  ┌────────────────────────────────────────────────────────────┐  ║
║  │  "labs_access": [ "watchlist-growthforge" ]                │  ║
║  └────────────────────────────────────────────────────────────┘  ║
║                                                                  ║
║  Users without this privilege will not see the GrowthForge       ║
║  controls or receive suggestions. No data is generated or        ║
║  cached for unauthorized accounts.                               ║
║                                                                  ║
╠══════════════════════════════════════════════════════════════════╣
║  sys::growthforge v0.1.0 ░░░░░░░░░░░░░░░░░▓▓▓ ONLINE ▓▓▓`}
                    <Box
                      component="span"
                      sx={{
                        "@keyframes blink": {
                          "0%, 50%": { opacity: 1 },
                          "51%, 100%": { opacity: 0 },
                        },
                        animation: "blink 1s infinite",
                      }}
                    >
                      █
                    </Box>
                    {`     ║
╚══════════════════════════════════════════════════════════════════╝`}
                  </Box>
                </Box>
              </HoloPad>
            </Box>
          )
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<MenuBookIcon />}
          component={Link}
          to="/getting-started"
        >
          Getting Started Guide
        </Button>
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          href="https://gitlab.com/tveyes/internal/twosion/insight-labs"
          target="_blank"
          rel="noopener noreferrer"
        >
          View GitLab Group
        </Button>
      </Box>
    </Box>
  );
}
